import { useEffect, useState, useCallback } from 'react';
import { User, useDeleteUser, useEmailAddressAvailable, useUpdateUser } from '../../../hooks/useUsers';
import debounce from '../../../components/common/debounce';
import { CustomInput, DropDown, ToggleButton } from '../../../components/common';
import Modal from '../../../components/common/modal';
import AlertBox from '../../../components/route-leaving-prompt/alert-box';
import { normalizeMobilePhone, validateMobilePhone } from '../../../components/common/phone-validation';
import { toast } from 'react-toastify';
import { useAppSettings } from '../../../hooks/useAppSettings';

type props = {
  user: User;
};

const userStatus = {
  enabled: 'Enabled',
  disabled: 'Disabled',
  under_review: 'Under Review',
  suspended: 'Suspended',
  banned: 'Banned',
};

const EditUserDetails: React.FunctionComponent<props> = ({ user }): JSX.Element => {
  const { data: appSettings } = useAppSettings();
  const { updateUser } = useUpdateUser();
  const deleteUser = useDeleteUser();
  const [modal, setModal] = useState<boolean>(false);
  const [alertModal, setAlertModal] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [userDetails, setUserDetails] = useState<User>();
  const [errorMessages, setErrorMessages] = useState<any>({});
  const [_enableSaveOption, setEnableSaveOption] = useState(false);
  const enableSaveOption = _enableSaveOption && Object.keys(errorMessages).length === 0;
  const emailDebounce = debounce(userDetails?.email_address, 1000);
  const { data: emailAvailable } = useEmailAddressAvailable(emailDebounce, {
    onSuccess: (result) => {
      if (!userDetails) return;
      const { email_address } = userDetails!;

      const emailRegex = /\S+@\S+\.\S+/;

      if (!emailRegex.test(email_address) || email_address === '') {
        return setErrorMessages({ email_address: 'Enter valid email address' });
      } else if (!result && user.email_address !== email_address) {
        setErrorMessages({ email_address: 'Email already used, use different email' });
      } else if (result) {
        setErrorMessages({});
      }
    },
  });

  const setDefaultOptions = useCallback(() => {
    if (!user) return;
    const { mobile_phone, first_name, last_name, display_name, gender, timezone, email_address, test_user } = user;
    const mobilePhoneInput = mobile_phone.replace('+1', '');
    setUserDetails(() => ({
      ...user,
      mobile_phone: normalizeMobilePhone(mobilePhoneInput),
      first_name,
      last_name,
      display_name,
      gender,
      timezone,
      email_address,
      test_user,
    }));
    return userDetails;
  }, [user]);

  const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    } else {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  };

  const updateUserDetails = async (name: string, value: string | number | null | boolean) => {
    await validateUserDetails();
    setEnableSaveOption(true);
    setUserDetails((prev: any) => ({ ...prev, [name]: value }));
  };

  const validateUserDetails = async () => {
    if (!userDetails) return;
    let isValid = false;
    try {
      validateEmail();
      const { mobile_phone } = userDetails;
      //need to hit the UI to get this immediately
      const updatedPhone = (document.getElementById('user-edit-phone') as HTMLInputElement).value;
      if (!validatePhone(updatedPhone)) {
        return;
      }

      if ((!mobile_phone || mobile_phone.trim() === '') && validateEmail()) {
        setErrorMessages({});
        isValid = true;
      } else {
        let parsedMobilePhone = mobile_phone;
        //we need to convert numbers to xxx-xxx-xxxx format to send them to the endpoint
        if (parsedMobilePhone.includes('(') || parsedMobilePhone.includes(')') || parsedMobilePhone.includes(' ')) {
          parsedMobilePhone = mobile_phone.replaceAll('(', '').replaceAll(')', '').replaceAll(' ', '-');
          setUserDetails((prev: any) => ({ ...prev, mobile_phone: parsedMobilePhone }));
        }
        //don't hammer the endpoint until our number is ready
        if (parsedMobilePhone.length === 12) {
          const phoneErrorMessage = await validateMobilePhone(parsedMobilePhone); // extract a return val from this guy
          if (phoneErrorMessage !== '') {
            setErrorMessages({ mobile_phone: phoneErrorMessage });
          }
        }
      }
    } catch (e) {}

    return isValid;
  };

  const validateEmail = () => {
    if (!userDetails || !user) return;
    const { email_address } = userDetails;

    const emailRegex = /\S+@\S+\.\S+/;

    if (!emailRegex.test(email_address) || email_address === '') {
      return setErrorMessages({ email_address: 'Enter valid email address' });
    } else if (!emailAvailable && user.email_address !== email_address) {
      return setErrorMessages({
        email_address: 'Email already used, use different email',
      });
    }
    setErrorMessages({});
    return true;
  };

  const validatePhone = (mobile_phone: string) => {
    const phoneRegexHyphens = new RegExp(/^\d{3}-\d{3}-\d{4}$/);
    const phoneRegexParentheses = new RegExp(/^\(\d{3}\) \d{3}-\d{4}$/);

    if (!phoneRegexHyphens.test(mobile_phone) && !phoneRegexParentheses.test(mobile_phone)) {
      return setErrorMessages({
        mobile_phone: 'Phone number not in xxx-xxx-xxxx or (xxx) xxx-xxxx format.',
      });
    }
    return true;
  };

  const handleDelete = async () => {
    if (!userDetails || deleteUser.isLoading) return;
    deleteUser.mutate(userDetails.id, {
      onSuccess: (response) => {
        const outcome = response?.data?.outcome;
        toast.success(
          outcome === 'anonymized'
            ? 'Account removed; call, connection, and payout history was kept and anonymized.'
            : 'Account removed.',
        );
        setDeleteModal(false);
        setModal(false);
      },
      onError: () => {
        toast.error('User removal failed. Please contact the development team for more information.');
      },
    });
  };

  const handleSave = async () => {
    const payload: Partial<User> = {
      ...userDetails,
    };

    if (payload.mobile_phone?.trim() === '') {
      payload.mobile_phone = undefined;
    }

    updateUser.mutate(
      { userId: user.id, data: payload },
      {
        onSuccess: () => {
          toast.success('User updated successfully');
        },
      },
    );
    setModal(false);
    setAlertModal(false);
    setEnableSaveOption(false);
  };

  useEffect(() => {
    if (modal && user) {
      setDefaultOptions();
    }
  }, [modal, user, setDefaultOptions]);

  return (
    <>
      <button
        onClick={(event) => {
          event.stopPropagation();
          setModal(true);
        }}
        className="bg-blue-primary h-10 rounded-sm px-2 text-center text-sm text-white"
      >
        Edit
      </button>
      <Modal
        isModel={modal}
        InnerComponent={
          <div
            onClick={(event) => {
              event.stopPropagation();
            }}
            className="inline-block transform overflow-hidden bg-white align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl sm:align-middle"
          >
            <div className="bg-white px-2 pb-2 pt-3 sm:p-6 sm:pb-4">
              <div className="flex justify-start">
                <div>
                  <h3 className="blue-primary py-1 text-2xl font-normal">Edit User</h3>
                </div>
              </div>
              <div className="flex-start flex py-2">
                <h3 className="text-gray-dark py-1 text-lg font-bold">Details</h3>
              </div>
              <div className="mb-16 mr-5 grid grid-cols-3 gap-3 gap-y-16">
                <div>
                  <CustomInput
                    inputType={'text'}
                    value={userDetails?.first_name}
                    setValue={(data) => updateUserDetails('first_name', data)}
                    Label={'First Name'}
                    dataTestId="user-edit-first-name"
                  />
                </div>
                <div>
                  <CustomInput
                    inputType={'text'}
                    value={userDetails?.last_name}
                    setValue={(data) => updateUserDetails('last_name', data)}
                    Label={'Last Name'}
                    dataTestId="user-edit-last-name"
                  />
                </div>
                <div>
                  <CustomInput
                    inputType={'text'}
                    value={userDetails?.display_name}
                    setValue={(data) => updateUserDetails('display_name', data)}
                    Label={'Display Name'}
                    dataTestId="user-edit-display-name"
                  />
                </div>
                <div>
                  <CustomInput
                    inputType={'tel'}
                    value={userDetails?.mobile_phone}
                    setValue={(data) => updateUserDetails('mobile_phone', formatPhoneNumber(data))}
                    Label={'Phone'}
                    Error={errorMessages.mobile_phone?.length > 0 ? true : false}
                    ErrorMessage={errorMessages.mobile_phone}
                    dataTestId="user-edit-phone"
                  />
                </div>
                <div>
                  <CustomInput
                    inputType={'text'}
                    value={userDetails?.email_address}
                    setValue={(data) => updateUserDetails('email_address', data)}
                    Label={'Email'}
                    Error={errorMessages.email_address?.length > 0 ? true : false}
                    ErrorMessage={errorMessages.email_address}
                    dataTestId="user-edit-email"
                  />
                </div>
                <div>
                  <DropDown
                    Label={'Timezone'}
                    labelClass="float-left block leading-4 text-gray-dark mb-2"
                    value={userDetails?.timezone}
                    setValue={(data) => {
                      setEnableSaveOption(true);
                      setUserDetails((prev: any) => ({
                        ...prev,
                        timezone: data === '' ? null : data,
                      }));
                    }}
                    data-testid="user-edit-timezone"
                  >
                    <>
                      <option key={'timezone'} value={''}>
                        Select TimeZone
                      </option>
                      {appSettings?.supported_timezones &&
                        appSettings.supported_timezones?.map((timezone: string) => (
                          <option key={timezone} value={timezone}>
                            {timezone}
                          </option>
                        ))}
                    </>
                  </DropDown>
                </div>
              </div>
              <div className="mr-5 mt-2 grid grid-cols-3 gap-3">
                <div>
                  <DropDown
                    Label={'Account Status'}
                    labelClass="float-left block leading-4 text-gray-dark mb-2"
                    value={userDetails?.status}
                    setValue={(data) => {
                      updateUserDetails('status', data);
                    }}
                    data-testid="user-edit-status"
                  >
                    <>
                      {Object.keys(userStatus).map((item: string) => (
                        <option key={item} value={item}>
                          {/* @ts-ignore */}
                          {userStatus[item]}
                        </option>
                      ))}
                    </>
                  </DropDown>
                </div>
                <div>
                  <DropDown
                    Label={'Gender'}
                    labelClass="float-left block leading-4 text-gray-dark mb-2"
                    value={userDetails?.gender}
                    setValue={(data) => updateUserDetails('gender', data)}
                    data-testid="user-edit-gender"
                  >
                    <option value={'Male'}>Male</option>
                    <option value={'Female'}>Female</option>
                    <option value={'Non Binary'}>Non Binary</option>
                    <option value={'Agender'}>Agender</option>
                    <option value={'Other'}>Other</option>
                    <option value={'Prefer not to say'}>Prefer not to say</option>
                  </DropDown>
                </div>
                <div className="pl-4">
                  <div className="text-md text-gray-dark float-left pr-10">For testing:</div>
                  <div className="">
                    <ToggleButton
                      id={'testUser'}
                      status={userDetails?.test_user}
                      initialstatus={false}
                      setStatus={(data: boolean) => {
                        updateUserDetails('test_user', data);
                      }}
                      data-testid="user-edit-test-user"
                    ></ToggleButton>
                  </div>
                </div>
              </div>
              <div className="mt-16 flex justify-between">
                <button
                  className={
                    'bg-red h-8 min-w-16 rounded-sm px-2 text-center text-sm text-white ' +
                    (enableSaveOption ? 'bg-blue-primary' : 'bg-gray-200')
                  }
                  onClick={() => setDeleteModal(true)}
                  data-testid="user-edit-delete-button"
                >
                  Remove
                </button>
                <div>
                  <button
                    className={
                      'h-8 w-16 rounded-sm px-2 text-center text-sm text-white ' +
                      (enableSaveOption ? 'bg-blue-primary' : 'bg-gray-200')
                    }
                    onClick={handleSave}
                    disabled={!enableSaveOption}
                    data-testid="user-edit-save-button"
                  >
                    Save
                  </button>
                  <button
                    className="bg-blue-primary ml-4 h-8 w-16 rounded-sm px-2 text-center text-sm text-white"
                    onClick={() => {
                      if (enableSaveOption) {
                        setAlertModal(true);
                      } else {
                        setModal(false);
                      }
                    }}
                    data-testid="user-edit-cancel-button"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        }
      />
      <AlertBox
        visible={alertModal}
        handlePrompt={() => {
          setDefaultOptions();
          setModal(false);
          setAlertModal(false);
          setErrorMessages({});
          setEnableSaveOption(false);
        }}
        handleContinueSave={handleSave}
        closeModal={() => {
          setAlertModal(false);
        }}
        confirmButtonText={'Discard Changes'}
        titleText={'Alert'}
        contentText={'You have unsaved changes.  If you leave this screen without saving, your changes will be lost.'}
        confirmSaveButtonText={'Save Changes'}
        cancelButtonText={'Cancel'}
      />
      <AlertBox
        visible={deleteModal}
        handlePrompt={() => {
          handleDelete();
        }}
        closeModal={() => {
          setDeleteModal(false);
        }}
        titleText={'Remove user account'}
        contentText={
          'This is a best-effort removal, not a wipe of every related row. Login and personal information are removed, and this peer’s videos and experiences are deleted. Call, connection, and payout history may remain in anonymized form. Stripe records are kept for financial and tax reasons.'
        }
        confirmButtonText={deleteUser.isLoading ? 'Removing…' : 'Remove user'}
        cancelButtonText={'Cancel'}
      />
    </>
  );
};

export default EditUserDetails;
